import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
interface CountdownTimerProps {
  targetDate: Date;
  className?: string;
}
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  className = ''
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isExpired, setIsExpired] = useState(false);
  const isMobile = useIsMobile();
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = targetDate.getTime() - now;
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
        const minutes = Math.floor(difference % (1000 * 60 * 60) / (1000 * 60));
        const seconds = Math.floor(difference % (1000 * 60) / 1000);
        setTimeLeft({
          days,
          hours,
          minutes,
          seconds
        });
        setIsExpired(false);
      } else {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
        setIsExpired(true);
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  // Check if this is being used in the top bar (has bg-transparent class)
  const isTopBar = className.includes('bg-transparent');
  if (isExpired) {
    return <div className={`text-center p-4 md:p-6 ${isTopBar ? 'bg-transparent text-white' : 'bg-red-50 border border-red-200 rounded-lg'} ${className}`}>
        <h3 className={`text-xl md:text-2xl font-bold ${isTopBar ? 'text-white' : 'text-red-600'} mb-2`}>Offer Expired</h3>
        <p className={`${isTopBar ? 'text-white/80' : 'text-red-500'} text-sm md:text-base`}>This special offer has ended.</p>
      </div>;
  }
  if (isTopBar) {
    return <div className={`text-center ${className}`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Clock className="text-white w-4 h-4 md:w-5 md:h-5" />
          <h3 className="text-sm md:text-lg font-bold text-white">Limited Special Offer Ends In:</h3>
        </div>
        
        <div className={`flex items-center justify-center gap-2 md:gap-4 text-white`}>
          <div className="flex flex-col items-center">
            <div className="text-lg md:text-2xl font-bold">
              {timeLeft.days.toString().padStart(2, '0')}
            </div>
            <div className="text-xs text-white/80">Days</div>
          </div>
          
          <div className="text-lg md:text-2xl font-bold">:</div>
          
          <div className="flex flex-col items-center">
            <div className="text-lg md:text-2xl font-bold">
              {timeLeft.hours.toString().padStart(2, '0')}
            </div>
            <div className="text-xs text-white/80">Hours</div>
          </div>
          
          <div className="text-lg md:text-2xl font-bold">:</div>
          
          <div className="flex flex-col items-center">
            <div className="text-lg md:text-2xl font-bold">
              {timeLeft.minutes.toString().padStart(2, '0')}
            </div>
            <div className="text-xs text-white/80">Minutes</div>
          </div>
          
          <div className="text-lg md:text-2xl font-bold">:</div>
          
          <div className="flex flex-col items-center">
            <div className="text-lg md:text-2xl font-bold">
              {timeLeft.seconds.toString().padStart(2, '0')}
            </div>
            <div className="text-xs text-white/80">Seconds</div>
          </div>
        </div>
      </div>;
  }
  return <div className={`text-center p-4 md:p-6 bg-[#FEE6D8] border border-[#003a70]/20 rounded-lg ${className}`}>
      <div className="flex items-center justify-center gap-2 mb-4">
        <Clock className="text-[#003a70] w-5 h-5 md:w-6 md:h-6" />
        <h3 className="text-lg md:text-2xl font-bold text-[#003a70]">Limited Time Offer</h3>
      </div>
      
      <div className={`grid grid-cols-4 gap-2 md:gap-4 ${isMobile ? 'max-w-xs' : 'max-w-md'} mx-auto`}>
        <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm border border-[#003a70]/10">
          <div className="text-lg md:text-2xl lg:text-3xl font-bold text-[#003a70]">
            {timeLeft.days.toString().padStart(2, '0')}
          </div>
          <div className="text-xs md:text-sm text-gray-600 font-medium">Days</div>
        </div>
        
        <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm border border-[#003a70]/10">
          <div className="text-lg md:text-2xl lg:text-3xl font-bold text-[#003a70]">
            {timeLeft.hours.toString().padStart(2, '0')}
          </div>
          <div className="text-xs md:text-sm text-gray-600 font-medium">Hours</div>
        </div>
        
        <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm border border-[#003a70]/10">
          <div className="text-lg md:text-2xl lg:text-3xl font-bold text-[#003a70]">
            {timeLeft.minutes.toString().padStart(2, '0')}
          </div>
          <div className="text-xs md:text-sm text-gray-600 font-medium">Minutes</div>
        </div>
        
        <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm border border-[#003a70]/10">
          <div className="text-lg md:text-2xl lg:text-3xl font-bold text-[#003a70]">
            {timeLeft.seconds.toString().padStart(2, '0')}
          </div>
          <div className="text-xs md:text-sm text-gray-600 font-medium">Seconds</div>
        </div>
      </div>
      
      <p className="mt-4 text-xs md:text-sm text-gray-600">
        Offer ends Saturday, May 31st at 12:00 AM EST
      </p>
    </div>;
};
export default CountdownTimer;